import Link from 'next/link';
import { Button } from '@savvy-kit/components/button';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { FadeIn } from '@savvy-kit/foundations/motion/chart-motion';
import { PulseRing } from '@savvy-kit/foundations/motion/pulse-ring';
import { DELAY_STAT_STEP } from '@savvy-kit/foundations/motion/tokens';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { ArrowRight, CircleCheck, TriangleAlert } from 'lucide-react';

import { useTrackEvent } from '@savvy/deprecated-shared/tracking/use-track-event';

import type { HomeTaskRow } from './types';
import { CLIENT_PORTAL_RETRY_DESCRIPTION } from '../common/constants';
import { PortalCardError } from '../common/portal-card-error';
import { PortalCard, PortalCardFooter } from '../common/portal-section';
import { CLIENT_PORTAL_TASKS_ROUTE } from '../shell/constants';
import {
  HOME_TASK_ROW_ELEMENT_NAME,
  HOME_TASKS_DELAY,
  HOME_TASKS_EMPTY_MESSAGE,
  HOME_TASKS_ERROR_TITLE,
  HOME_TASKS_PANEL_CAP,
  HOME_TASKS_PANEL_SHEEN_CLASS,
  HOME_TASKS_PANEL_TITLE,
  HOME_TASKS_VIEW_ALL_LABEL,
} from './constants';

type HomeTasksPanelProps = {
  readonly loading: boolean;
  readonly failed: boolean;
  readonly onRetry: () => void;
  readonly tasks: readonly HomeTaskRow[];
  readonly delayOffset?: number;
};

const SKELETON_ROWS = Array.from({ length: HOME_TASKS_PANEL_CAP }, (_unused, index) => `task-placeholder-${index}`);

// One placeholder per capped row, each the height of a real task row, plus the footer's slot, so the panel
// keeps its size when the list lands.
function TasksSkeleton() {
  return (
    <>
      <div className="divide-y divide-border-light">
        {SKELETON_ROWS.map((row) => (
          <div key={row} className="px-6 py-4">
            <Skeleton className="h-5 w-full" />
          </div>
        ))}
      </div>
      <PortalCardFooter className="p-3">
        <div className="h-8" />
      </PortalCardFooter>
    </>
  );
}

function TaskDue({ due }: { readonly due: HomeTaskRow['due'] }) {
  if (!due.overdue) {
    return (
      <Text variant="body-sm" color="secondary" className="shrink-0 whitespace-nowrap">
        {due.label}
      </Text>
    );
  }
  return (
    <span className="flex shrink-0 items-center gap-1 whitespace-nowrap text-destructive">
      <TriangleAlert size={14} aria-hidden="true" />
      <Text variant="body-sm" color="destructive">
        {due.label}
      </Text>
    </span>
  );
}

// A status light on each open task, breathing so the panel reads as waiting on someone; overdue breathes
// faster and stronger.
function TaskStatusDot({ overdue }: { readonly overdue: boolean }) {
  return (
    <span
      aria-hidden="true"
      data-slot="task-status-dot"
      className={cn('relative mt-1.5 flex size-2.5 shrink-0 rounded-full', overdue ? 'bg-destructive' : 'bg-success')}
    >
      <PulseRing tone={overdue ? 'var(--destructive)' : 'var(--success)'} urgent={overdue} />
    </span>
  );
}

type TaskRowsProps = {
  readonly tasks: readonly HomeTaskRow[];
  readonly delayOffset: number;
};

function TaskRows({ tasks, delayOffset }: TaskRowsProps) {
  const trackInteraction = useTrackEvent('userInteraction');

  return (
    <ul className="divide-y divide-border-light">
      {tasks.slice(0, HOME_TASKS_PANEL_CAP).map((task, index) => (
        <FadeIn key={task.id} delay={delayOffset + HOME_TASKS_DELAY + index * DELAY_STAT_STEP} className="block">
          <li>
            <Link
              href={task.href}
              onClick={() => {
                trackInteraction({
                  elementName: HOME_TASK_ROW_ELEMENT_NAME,
                  htmlElement: 'link',
                  interactionType: 'button_click',
                });
              }}
              className="flex items-start justify-between gap-4 px-6 py-4 transition-colors hover:bg-component-hover"
            >
              <span className="flex min-w-0 flex-1 items-start gap-3">
                <TaskStatusDot overdue={task.due.overdue} />
                <Text variant="body-sm" color="dark" className="min-w-0 flex-1 break-words font-medium leading-snug">
                  {task.name}
                </Text>
              </span>
              <TaskDue due={task.due} />
            </Link>
          </li>
        </FadeIn>
      ))}
    </ul>
  );
}

function ViewAllTasksFooter() {
  return (
    <PortalCardFooter className="p-3">
      <Button
        variant="ghost"
        size="sm"
        className="w-full justify-between"
        render={<Link href={CLIENT_PORTAL_TASKS_ROUTE} />}
      >
        {HOME_TASKS_VIEW_ALL_LABEL}
        <ArrowRight aria-hidden="true" />
      </Button>
    </PortalCardFooter>
  );
}

function TasksEmptyState() {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
      <span
        aria-hidden="true"
        className="flex size-11 items-center justify-center rounded-lg bg-success-background text-success-foreground-dark"
      >
        <CircleCheck size={22} />
      </span>
      <Text variant="body" color="dark" className="max-w-40 leading-snug">
        {HOME_TASKS_EMPTY_MESSAGE}
      </Text>
    </div>
  );
}

export function HomeTasksPanel({ loading, failed, onRetry, tasks, delayOffset = 0 }: HomeTasksPanelProps) {
  if (loading) {
    return (
      <PortalCard title={HOME_TASKS_PANEL_TITLE} padded={false} className={HOME_TASKS_PANEL_SHEEN_CLASS}>
        <TasksSkeleton />
      </PortalCard>
    );
  }

  if (failed) {
    return (
      <PortalCard title={HOME_TASKS_PANEL_TITLE} padded={false}>
        <PortalCardError
          title={HOME_TASKS_ERROR_TITLE}
          description={CLIENT_PORTAL_RETRY_DESCRIPTION}
          onRetry={onRetry}
        />
      </PortalCard>
    );
  }

  if (tasks.length === 0) {
    return (
      <PortalCard title={`${HOME_TASKS_PANEL_TITLE} (0)`} padded={false} className={HOME_TASKS_PANEL_SHEEN_CLASS}>
        <TasksEmptyState />
        <ViewAllTasksFooter />
      </PortalCard>
    );
  }

  return (
    <PortalCard
      title={`${HOME_TASKS_PANEL_TITLE} (${tasks.length})`}
      padded={false}
      className={HOME_TASKS_PANEL_SHEEN_CLASS}
    >
      <TaskRows tasks={tasks} delayOffset={delayOffset} />
      <ViewAllTasksFooter />
    </PortalCard>
  );
}
